import { Injectable, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Observable } from "rxjs/Observable";
import { Type, Device, HistoricVar, QueryReadings } from "./device.model";
import { Device as Device_new } from "./devices.model_new";
import "rxjs/add/operator/map";
import { ApiService } from "../../core/services/api.service";
import { DataDTO } from "../../shared/DTO/DataDTO";
import { SessionStateService } from "../../core/services/session-state.service";
import { DatePipe } from "@angular/common";
import { HvacDTO } from "../../shared/DTO/HvacDTO";
import { HistoricVarsDTO } from "../../shared/DTO/HistoricVarsDTO";
import { SubscribedCostPlansDTO } from "../../shared/DTO/SubscribedCostPlansDTO";
import { DeviceDTO } from "../../shared/DTO/DeviceDTO";
import { GeneralService } from "../../shared/services/general.service";
import { DatePipeProxy } from "../../shared/translate/datePipeProxy.component";
import { DeviceType, DeviceTypesMap, GROUP } from "./devices.constant";
import { HttpParams } from "@angular/common/http";
import { ModuleDTO } from "../../shared/DTO/ModuleDTO";
import { StateVariables } from "./state-variables.model";
import { ClientTariff } from "../../shared/constants";
import { HVACModelDTO } from "../../shared/DTO/ModelDTO";

@Injectable()
export class DevicesService {
  constructor(
    private router: Router,
    private apiService: ApiService,
    private sessionStateService: SessionStateService,
    private generalService: GeneralService
  ) {}

  getHouseDevice(houseId: string) {
    return this.apiService.get(`/equipment/houses/${houseId}/device`);
  }

  getHouseDeviceTypes(houseId: string): Observable<DeviceType[]> {
    return this.getHouseModules(houseId).map((res) =>
      Device_new.extractTypes(res)
    );
  }

  getHouseModules(
    houseId: string,
    deviceType?: DeviceType,
    categoryId?: string,
    groupOrFilter?: GROUP
  ): Observable<Device_new[]> {
    const paramsMap: Map<string, string> = new Map();
    if (
      deviceType &&
      (deviceType !== DeviceType.CONSUMPTION || categoryId === undefined)
    ) {
      const deviceTypeDefinition = DeviceTypesMap.map.get(deviceType);
      paramsMap.set(
        "groupsorfilter",
        JSON.stringify(
          groupOrFilter ? [groupOrFilter] : deviceTypeDefinition.groups
        )
      );
      paramsMap.set(
        "groupsnotfilter",
        JSON.stringify(deviceTypeDefinition.nonGroups)
      );
    }

    if (
      deviceType &&
      deviceType === DeviceType.CONSUMPTION &&
      categoryId !== undefined
    ) {
      paramsMap.set("categoryfilter", categoryId);
    }

    if (deviceType === null && categoryId === null && groupOrFilter) {
      paramsMap.set("groupsorfilter", JSON.stringify([groupOrFilter]));
    }

    let params = new HttpParams();
    paramsMap.forEach((value: any, key: any) => {
      params = params.set(key, value);
    });

    return this.apiService
      .get(`/equipment/houses/${houseId}/modules`, { params: params })
      .map((res) => Device_new.fromDTOWrapper(res));
  }

  getHouseModulesByGroups(
    houseId: string,
    deviceType?: DeviceType,
    categoryId?: string,
    groupOrFilter?: GROUP[]
  ): Observable<Device_new[]> {
    const paramsMap: Map<string, string> = new Map();
    if (
      deviceType &&
      (deviceType !== DeviceType.CONSUMPTION || categoryId === undefined)
    ) {
      const deviceTypeDefinition = DeviceTypesMap.map.get(deviceType);
      paramsMap.set(
        "groupsorfilter",
        JSON.stringify(
          groupOrFilter ? groupOrFilter : deviceTypeDefinition.groups
        )
      );
      paramsMap.set(
        "groupsnotfilter",
        JSON.stringify(deviceTypeDefinition.nonGroups)
      );
    }

    if (
      deviceType &&
      deviceType === DeviceType.CONSUMPTION &&
      categoryId !== undefined
    ) {
      paramsMap.set("categoryfilter", categoryId);
    }

    let params = new HttpParams();
    paramsMap.forEach((value: any, key: any) => {
      params = params.set(key, value);
    });

    return this.apiService
      .get(`/equipment/houses/${houseId}/modules`, { params: params })
      .map((res) => Device_new.fromDTOWrapper(res));
  }

  getHouseModule(houseId: string, moduleId: string): Observable<Device_new> {
    return this.apiService
      .get(`/equipment/houses/${houseId}/modules/${moduleId}`)
      .map((res) => Device_new.fromDTO(res));
  }

  getSmartMeter(houseId: string): Observable<Device_new[]> {
    const paramsMap: Map<string, string> = new Map();
    paramsMap.set("groupsorfilter", JSON.stringify([GROUP.SMART_ENERGY_METER]));

    let params = new HttpParams();
    paramsMap.forEach((value: any, key: any) => {
      params = params.set(key, value);
    });

    return this.apiService
      .get(`/equipment/houses/${houseId}/modules`, { params: params })
      .map((res) => Device_new.fromDTOWrapper(res));
  }

  getEnergyStorage(houseId: string): Observable<Device_new[]> {
    const paramsMap: Map<string, string> = new Map();
    paramsMap.set("groupsorfilter", JSON.stringify([GROUP.ENERGY_STORAGE]));

    let params = new HttpParams();
    paramsMap.forEach((value: any, key: any) => {
      params = params.set(key, value);
    });

    return this.apiService
      .get(`/equipment/houses/${houseId}/modules`, { params: params })
      .map((res) => Device_new.fromDTOWrapper(res));
  }

  getGasMeter(houseId: string): Observable<Device_new> {
    const paramsMap: Map<string, string> = new Map();
    paramsMap.set("groupsorfilter", JSON.stringify([GROUP.GAS_METER]));

    let params = new HttpParams();
    paramsMap.forEach((value: any, key: any) => {
      params = params.set(key, value);
    });

    return this.apiService
      .get(`/equipment/houses/${houseId}/modules`, { params: params })
      .map((res) => Device_new.fromDTOWrapper(res)[0]);
  }

  getInjectionMeter(houseId: string): Observable<Device_new[]> {
    const paramsMap: Map<string, string> = new Map();
    paramsMap.set(
      "groupsandfilter",
      JSON.stringify([GROUP.INJECTION_METER, GROUP.SMART_ENERGY_METER])
    );

    let params = new HttpParams();
    paramsMap.forEach((value: any, key: any) => {
      params = params.set(key, value);
    });

    return this.apiService
      .get(`/equipment/houses/${houseId}/modules`, { params: params })
      .map((res) => Device_new.fromDTOWrapper(res));
  }

  getAlertsDevices(houseId: string): Observable<Device_new[]> {
    const paramsMap: Map<string, string> = new Map();
    paramsMap.set(
      "groupsorfilter",
      JSON.stringify([GROUP.METERING, GROUP.CONSUMPTION_METER])
    );
    paramsMap.set(
      "groupsnotfilter",
      JSON.stringify([GROUP.PRODUCTION_METER, GROUP.SMART_ENERGY_METER])
    );

    let params = new HttpParams();
    paramsMap.forEach((value: any, key: any) => {
      params = params.set(key, value);
    });

    return this.apiService
      .get(`/equipment/houses/${houseId}/modules`, { params: params })
      .map((res) => Device_new.fromDTOWrapper(res));
  }

  getDevicesOrder(): Map<string, string[]> {
    return this.sessionStateService.getDevicesOrder();
  }

  saveDevicesOrder(type: string, newOrder: string[]) {
    const orders: Map<string, string[]> = this.getDevicesOrder();
    orders.set(type, newOrder);
    this.sessionStateService.setDevicesOrder(orders);
  }
  public getAllHVacsBrands() {
    return this.apiService.get(`/interfacedata/modules/acmodels`);
  }

  public getHavcBrandsandModel(brand: string, model: string) {
    return this.apiService
      .get(
        `/interfacedata/modules/acmodels/brand/` + brand + "?acmodel=" + model
      )
      .map((hvac: any) => hvac.acCapabilities);
  }

  public executeCommand(
    pkid: string,
    key: string,
    value: string,
    inRealTime: boolean
  ): Observable<any> {
    const body = {
      Parameters: `{"key": "${key}", "value": "${value}"}`,
    };

    const service = `ExecuteCommand${inRealTime ? "InRealTime" : ""}`;

    return this.apiService.post(
      `/HA/{{sessionId}}/Modules(${pkid})/${service}`,
      body
    );
  }

  public updateModule(moduleId: string, body: any) {
    return this.apiService.patch(
      `/equipment/houses/${
        this.sessionStateService.getHouse().houseId
      }/modules/${moduleId}`,
      body
    );
  }

  public getMetering(
    deviceid: string,
    moduleid: string,
    resolution: string,
    start: string,
    end: string,
    historicVar: string
  ) {
    return this.apiService.get(
      `/meteringapi/house/${
        this.sessionStateService.getHouse().houseId
      }/device/${deviceid}/module/${moduleid}` +
        `/energy/graph?historicvar=${historicVar}&end=${end}&start=${start}&resolution=${resolution}`
    );
  }

  public getCostPerKwh(resolution: string, date: string) {
    return this.apiService.get(
      `/meteringapi/house/${
        this.sessionStateService.getHouse().houseId
      }/energy/graph/cost-per-kwh` + `?date=${date}&resolution=${resolution}`
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/entities/devices/devices.service.ts